'use client';

import { Button } from '@repo/ui/button';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import React, { memo, useState, useTransition } from 'react';
import PaymentStatus from '@/app/[locale]/(account)/account/components/payment-method-status';
import Method from '@/components/common/method-item';
import { APP_REVALIDATE_QUERY_NAMES, PAYMENT_TYPES } from '@/constants';
import { type FullMethod } from '@/hooks/usePaymentMethodsWithNetworks';
import { useTenant } from '@/providers/TenantProvider';
import { type DepositPayload, onDeposit, onDepositVerify } from '@/server-actions/deposit';
import GatewayCheckout from '../gateway-checkout';
import DepositInfoModal from '../info-modal';
import { AMOUNTS, type AmountType } from './constants';

type DepositFiatProps = {
  selectedMethod?: FullMethod;
};

const AmountPicker = ({ selectedMethod }: DepositFiatProps) => {
  const t = useTranslations(PAYMENT_TYPES.DEPOSITS);

  const {
    tenantConfig: {
      settings: { enableInformationPopupOnDeposit },
    },
  } = useTenant();

  const queryClient = useQueryClient();

  const [depositStatus, setDepositStatus] = useState<string | null>(null);

  const [selectedAmount, setSelectedAmount] = useState<AmountType>(
    AMOUNTS?.[selectedMethod?.currencyCode as string]?.[1]
  );

  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);

  const [isPending, startTransition] = useTransition();

  const handleDeposit = async () => {
    startTransition(async () => {
      const payload = {
        amount: selectedAmount?.value?.toString(),
        currency: selectedMethod?.currencyCode,
        providerId: selectedMethod?.providerId,
        paymentGatewayMethod: selectedMethod?.paymentGatewayMethod,
        providerName: selectedMethod?.name,
      };

      const response = await onDeposit(payload as DepositPayload);

      if (response.checkoutUrl) {
        setCheckoutUrl(response.checkoutUrl);
      }
    });
  };

  const handleIFrameRedirectionToHost = async (orderId: string | null) => {
    if (!orderId) return;

    const response = await onDepositVerify({
      orderId,
      centralPayPaymentMethod: selectedMethod?.paymentGatewayMethod as 'C2C' | 'IPG' | 'CPG',
    });

    if (response.success) {
      queryClient.invalidateQueries({ queryKey: [APP_REVALIDATE_QUERY_NAMES.WALLET_BALANCE] });
    }

    queryClient.invalidateQueries({ queryKey: [APP_REVALIDATE_QUERY_NAMES.DEPOSITS_HISTORY] });

    setDepositStatus(response.success ? 'SUCCESS' : 'ERROR');
  };

  if (depositStatus) {
    return (
      <PaymentStatus
        paymentType={PAYMENT_TYPES.DEPOSITS}
        amount={selectedAmount.value}
        status={depositStatus}
        currency={selectedMethod?.currencyCode || ''}
        goBack={() => setDepositStatus(null)}
      />
    );
  }

  if (checkoutUrl) {
    return (
      <GatewayCheckout
        onIFrameRedirectionToHost={handleIFrameRedirectionToHost}
        checkoutUrl={checkoutUrl!}
      />
    );
  }

  return (
    <>
      <div className="flex h-full w-full flex-col items-center justify-center space-y-10 animate-in fade-in sm:px-[10%]">
        <div className="relative top-3 flex w-full justify-center text-center vt-text-size-medium vt-headings-text">
          <span>{t('fiat-description-1')}</span>
        </div>

        <div className="grid w-full grid-cols-2 gap-4 text-center sm:grid-cols-3">
          {AMOUNTS[selectedMethod?.currencyCode as string]?.map((item, i) => {
            const { value, label } = item;

            if (i === AMOUNTS[selectedMethod?.currencyCode as string].length - 1) {
              return (
                <div
                  key={value}
                  className="col-span-2 sm:col-span-3"
                >
                  <Method
                    title={`${selectedMethod?.currencyCode} ${label}`}
                    isActive={selectedAmount?.value === value}
                    onClick={() => setSelectedAmount(item)}
                  />
                </div>
              );
            }
            return (
              <Method
                key={value}
                title={`${selectedMethod?.currencyCode} ${label}`}
                isActive={selectedAmount?.value === value}
                onClick={() => setSelectedAmount(item)}
              />
            );
          })}
        </div>

        <div className="flex w-full justify-center text-center vt-text-size-medium vt-headings-text sm:vt-text-size-medium-sm">
          <span>{t('fiat-description-2')}</span>
        </div>

        <Button
          loading={isPending}
          onClick={handleDeposit}
          className="w-full rounded-full sm:w-auto"
        >
          {t('fiat-submit-button-label')}
        </Button>
      </div>
      {enableInformationPopupOnDeposit && <DepositInfoModal open />}
    </>
  );
};

export default memo(AmountPicker);
